import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { OfferDto, OfferListItemDto, ServiceDto, SizeKey } from '@shared/types';

export const offersKey = ['offers'] as const;
export const offerKey = (id: string) => ['offer', id] as const;

export function useOffers() {
  return useQuery({
    queryKey: offersKey,
    queryFn: () => api.get<{ data: OfferListItemDto[] }>('/offers').then((r) => r.data),
  });
}

export function useOffer(id: string) {
  return useQuery({
    queryKey: offerKey(id),
    queryFn: () => api.get<{ data: OfferDto }>(`/offers/${id}`).then((r) => r.data),
  });
}

export function useServices() {
  return useQuery({
    queryKey: ['services'],
    queryFn: () => api.get<{ data: ServiceDto[] }>('/services').then((r) => r.data),
    staleTime: 5 * 60_000,
  });
}

/**
 * Mutace nad jednou nabídkou. Endpointy vrací vždy celou nabídku včetně přepočtených
 * součtů, takže odpověď rovnou zapíšeme do cache — žádný refetch a náhled se
 * překreslí okamžitě.
 */
export function useOfferMutations(id: string) {
  const qc = useQueryClient();
  const store = (res: { data: OfferDto }) => {
    qc.setQueryData(offerKey(id), res.data);
    qc.invalidateQueries({ queryKey: offersKey });
    return res.data;
  };

  return {
    updateOffer: useMutation({
      mutationFn: (patch: Partial<OfferDto>) =>
        api.patch<{ data: OfferDto }>(`/offers/${id}`, patch).then(store),
    }),
    addFromCatalog: useMutation({
      mutationFn: (payload: { serviceId: string; sizeKey: SizeKey; quantity?: number }) =>
        api
          .post<{ data: OfferDto }>(`/offers/${id}/items`, { fromService: payload })
          .then(store),
    }),
    addCustomItem: useMutation({
      mutationFn: (payload: { title: string; amount: number }) =>
        api.post<{ data: OfferDto }>(`/offers/${id}/items`, payload).then(store),
    }),
    updateItem: useMutation({
      mutationFn: ({ itemId, patch }: { itemId: string; patch: Record<string, unknown> }) =>
        api.patch<{ data: OfferDto }>(`/offers/${id}/items/${itemId}`, patch).then(store),
    }),
    removeItem: useMutation({
      mutationFn: (itemId: string) =>
        api.delete<{ data: OfferDto }>(`/offers/${id}/items/${itemId}`).then(store),
    }),
    reorderItems: useMutation({
      mutationFn: (order: string[]) =>
        api.patch<{ data: OfferDto }>(`/offers/${id}/items/reorder`, { order }).then(store),
    }),
    translate: useMutation({
      mutationFn: () =>
        api
          .post<{ data: OfferDto; stats: { fromCatalog: number; machine: number } }>(
            `/offers/${id}/translate`,
          )
          .then((res) => {
            store(res);
            return res.stats;
          }),
    }),
  };
}

/** Stáhne PDF přes backend (Browser Rendering) a nechá prohlížeč uložit soubor. */
export async function downloadPdf(offerId: string): Promise<void> {
  const res = await fetch(`/api/offers/${offerId}/pdf`, {
    method: 'POST',
    credentials: 'include',
  });

  if (!res.ok) {
    // Odpověď čteme jako text a teprve pak zkoušíme JSON — při chybě platformy
    // (HTML stránka Cloudflare) by `res.json()` spadlo a skutečná příčina by se ztratila.
    const raw = await res.text().catch(() => '');
    let message = '';
    try {
      message = (JSON.parse(raw) as { message?: string }).message ?? '';
    } catch {
      message = raw.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 300);
    }
    // Prázdná zpráva = UI doplní lokalizovanou hlášku se statusem.
    throw Object.assign(new Error(message), { status: res.status });
  }

  const blob = await res.blob();
  const name =
    res.headers.get('Content-Disposition')?.match(/filename="([^"]+)"/)?.[1] ?? 'nabidka.pdf';

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}
